import { Plus, Sprout, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { StatusBadge } from '@/components/ui/status-badge'
import { useAddSeed, useDeleteSeed } from '@/hooks/use-deliverability'
import type { SeedAccount } from '@/lib/types-deliverability'

interface SeedAccountsPanelProps {
  seeds: SeedAccount[]
  isLoading: boolean
}

export function SeedAccountsPanel({ seeds, isLoading }: SeedAccountsPanelProps): React.JSX.Element {
  const [showAdd, setShowAdd] = useState(false)
  const [email, setEmail] = useState('')
  const [provider, setProvider] = useState('')
  const addSeed = useAddSeed()
  const deleteSeed = useDeleteSeed()

  function handleAdd(): void {
    if (!email || !provider) return
    addSeed.mutate(
      { email, provider },
      {
        onSuccess: () => {
          setEmail('')
          setProvider('')
          setShowAdd(false)
        },
      },
    )
  }

  return (
    <div className="rounded-xl border border-surface-3 bg-surface-0">
      <div className="flex items-center justify-between border-b border-surface-3 px-5 py-4">
        <div className="flex items-center gap-2">
          <Sprout className="h-5 w-5 text-text-secondary" />
          <h2 className="text-lg font-medium text-text-primary">Seed Accounts</h2>
        </div>
        <button
          type="button"
          onClick={() => setShowAdd(!showAdd)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-surface-3 px-3 py-1.5 text-sm font-medium text-text-secondary transition-colors hover:bg-surface-1"
        >
          <Plus className="h-4 w-4" />
          Add Seed
        </button>
      </div>

      {showAdd && (
        <div className="flex items-end gap-3 border-b border-surface-2 bg-surface-1 px-5 py-4">
          <div className="flex-1">
            <label
              htmlFor="seed-email"
              className="mb-1 block text-xs font-medium text-text-secondary"
            >
              Email
            </label>
            <input
              id="seed-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="seed@gmail.com"
              className="w-full rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary"
            />
          </div>
          <div className="w-32">
            <label
              htmlFor="seed-provider"
              className="mb-1 block text-xs font-medium text-text-secondary"
            >
              Provider
            </label>
            <select
              id="seed-provider"
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              className="w-full rounded-lg border border-surface-3 bg-surface-0 px-3 py-2 text-sm text-text-primary"
            >
              <option value="">Select...</option>
              <option value="gmail">Gmail</option>
              <option value="outlook">Outlook</option>
              <option value="yahoo">Yahoo</option>
              <option value="icloud">iCloud</option>
              <option value="other">Other</option>
            </select>
          </div>
          <button
            type="button"
            onClick={handleAdd}
            disabled={addSeed.isPending || !email || !provider}
            className="rounded-lg bg-admin-500 px-4 py-2 text-sm font-medium text-white hover:bg-admin-600 disabled:opacity-50"
          >
            {addSeed.isPending ? 'Adding...' : 'Add'}
          </button>
        </div>
      )}

      <div className="divide-y divide-surface-2">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-admin-500 border-t-transparent" />
          </div>
        ) : seeds.length === 0 ? (
          <p className="py-8 text-center text-text-secondary">
            No seed accounts configured. Add one to start deliverability testing.
          </p>
        ) : (
          seeds.map((seed) => (
            <SeedRow key={seed.id} seed={seed} onDelete={() => deleteSeed.mutate(seed.id)} />
          ))
        )}
      </div>
    </div>
  )
}

function SeedRow({
  seed,
  onDelete,
}: {
  seed: SeedAccount
  onDelete: () => void
}): React.JSX.Element {
  return (
    <div className="flex items-center justify-between px-5 py-3">
      <div className="flex items-center gap-4">
        <div>
          <p className="text-sm font-medium text-text-primary">{seed.email}</p>
          <p className="text-xs text-text-tertiary capitalize">{seed.provider}</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        {seed.last_result ? (
          <StatusBadge status={seed.last_result} />
        ) : (
          <span className="text-xs text-text-tertiary">Not tested</span>
        )}
        <button
          type="button"
          onClick={onDelete}
          className="rounded-lg p-1.5 text-text-tertiary transition-colors hover:bg-red-50 hover:text-red-600"
          title="Delete seed account"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
